import React, { useRef } from 'react';
import { Box, Link } from '@mui/material';
import { CandidateFilterChip } from 'modules/candidate-profile/components/CandidateFilterChip';
import { CandidateFilterConnector } from 'modules/candidate-profile/components/CandidateFilterConnector';
import { candidateFilterSx } from 'modules/candidate-profile/styles/candidateFilterSx';
import { useFilterRailHeight } from 'modules/candidate-profile/hooks/useFilterRailHeight';
import { chipGroupsInOrder } from 'modules/candidate-profile/tables/filters/candidateFilterGroups';
import {
  CandidateFilterGroups,
  CandidateFilterMatchMode,
  CandidateFilterState,
  CandidateFilterValueType,
  CandidateRangeFilter
} from 'modules/candidate-profile/types/CandidateSearchFilter';
import { isRange } from 'modules/candidate-profile/tables/filters/candidateFilterState';
import { describeRangeConditionForChip } from 'modules/candidate-profile/tables/filters/describeRangeConditionForChip';

export type CandidateFilterChipItem = {
  id: string;
  field: string;
  label: string;
  mode: CandidateFilterMatchMode;
  values: string[];
};

type CandidateFilterChipRailProps = {
  filters: CandidateFilterState[];
  groups?: CandidateFilterGroups;
  /** Whether the table holds a filter. A chip can exist without one. */
  hasFilters: boolean;
  onJoinChipGroups?: (groupIndex: number) => void;
  onSplitChipGroup?: (groupIndex: number, chipIndex: number) => void;
  labelFor: (field: string) => string;
  isSearchColumn?: (field: string) => boolean;
  describeRangeForChip?: (filter: CandidateRangeFilter) => string;
  onRemove: (
    field: string,
    mode: CandidateFilterMatchMode,
    id?: string
  ) => void;
  onRemoveField?: (field: string) => void;
  onClearAll: () => void;
  onResetTableLayout: () => void;
  onSearchHistory?: () => void;
  onNewSearch?: () => void;
  /** Holds the search controls shut until the reply lands on the open search. */
  isReplying?: boolean;
  isAnchored?: boolean;
};

const isSelection = (
  filter: CandidateFilterState
): filter is Extract<CandidateFilterState, { values: string[] }> =>
  filter.type === CandidateFilterValueType.TEXT ||
  filter.type === CandidateFilterValueType.TEXT_ARRAY;

const isBoolean = (
  filter: CandidateFilterState
): filter is Extract<
  CandidateFilterState,
  { type: CandidateFilterValueType.BOOLEAN }
> => filter.type === CandidateFilterValueType.BOOLEAN;

const isGeo = (
  filter: CandidateFilterState
): filter is Extract<
  CandidateFilterState,
  { type: CandidateFilterValueType.GEO }
> => filter.type === CandidateFilterValueType.GEO;

export type Chip = {
  key: string;
  field: string;
  mode: CandidateFilterMatchMode;
  values: string[];
  isSearch?: boolean;
  hideMode?: boolean;
  removeByField?: boolean;
};

export const toChip = (
  filter: CandidateFilterState,
  isSearchColumn: (field: string) => boolean = () => false,
  describeRangeForChip: (filter: CandidateRangeFilter) => string = (range) =>
    describeRangeConditionForChip(range)
): Chip | null => {
  if (isSelection(filter)) {
    return {
      key: filter.id,
      field: filter.field,
      mode: filter.mode,
      values: filter.values.map(
        (value) => filter.valueLabels?.[value] ?? value
      ),
      isSearch:
        isSearchColumn(filter.field) &&
        filter.mode === CandidateFilterMatchMode.INCLUDE_ANY
    };
  }

  if (isBoolean(filter)) {
    return {
      key: filter.id,
      field: filter.field,
      mode: CandidateFilterMatchMode.INCLUDE_ANY,
      values: [filter.value ? 'Yes' : 'No'],
      removeByField: true
    };
  }

  // A radius reads as the place it resolved to; coordinates mean nothing here.
  if (isGeo(filter)) {
    return {
      key: filter.id,
      field: filter.field,
      mode: CandidateFilterMatchMode.INCLUDE_ANY,
      values: [
        filter.name
          ? `Within ${filter.radiusMiles} miles of ${filter.name}`
          : `Within ${filter.radiusMiles} miles`
      ],
      hideMode: true,
      removeByField: true
    };
  }

  if (isRange(filter)) {
    const text = describeRangeForChip(filter);
    if (!text) return null;

    return {
      key: filter.id,
      field: filter.field,
      mode: CandidateFilterMatchMode.INCLUDE_ANY,
      values: [text],
      hideMode: true,
      removeByField: true
    };
  }

  return null;
};

const linkSx = {
  fontSize: '16px',
  fontWeight: 400,
  lineHeight: '26px',
  color: 'primary.main',
  textDecorationColor: 'currentcolor',
  cursor: 'pointer',
  '&:disabled': {
    opacity: 0.5,
    cursor: 'default'
  }
};

export const CandidateFilterChipRail: React.FC<
  CandidateFilterChipRailProps
> = ({
  filters,
  groups = [],
  hasFilters,
  labelFor,
  isSearchColumn = () => false,
  describeRangeForChip,
  onRemove,
  onRemoveField,
  onJoinChipGroups,
  onSplitChipGroup,
  onClearAll,
  onResetTableLayout,
  onSearchHistory,
  onNewSearch,
  isReplying = false,
  isAnchored = false
}) => {
  const railRef = useRef<HTMLDivElement>(null);
  useFilterRailHeight(railRef, isAnchored);

  // Clearing a whole field would take a second condition on it with the
  // chip the recruiter clicked, so those come off by their own id instead.
  const sharedFields = new Set(
    filters
      .map((filter) => filter.field)
      .filter((field, at, all) => all.indexOf(field) !== at)
  );

  const renderChip = (chip: Chip) => (
    <CandidateFilterChip
      mode={chip.mode}
      label={labelFor(chip.field)}
      values={chip.values}
      isSearch={chip.isSearch}
      hideMode={chip.hideMode}
      onDelete={() => {
        if (chip.removeByField && !sharedFields.has(chip.field)) {
          return onRemoveField?.(chip.field);
        }
        return onRemove(chip.field, chip.mode, chip.key);
      }}
    />
  );

  const chipGroups = chipGroupsInOrder(filters, groups)
    .map((chipGroup) =>
      chipGroup
        .map((filter) => toChip(filter, isSearchColumn, describeRangeForChip))
        .filter((chip): chip is Chip => chip !== null)
    )
    .filter((chipGroup) => chipGroup.length > 0);

  return (
    <Box
      ref={railRef}
      sx={
        isAnchored
          ? candidateFilterSx.anchoredChipRail
          : candidateFilterSx.chipRail
      }
    >
      {chipGroups.length > 0 && (
        <Box sx={candidateFilterSx.chipRailChips}>
          {chipGroups.map((chipGroup, groupIndex) => (
            <React.Fragment key={chipGroup[0].key}>
              {groupIndex > 0 && (
                <CandidateFilterConnector
                  word="And"
                  onClick={() => onJoinChipGroups?.(groupIndex - 1)}
                />
              )}
              {chipGroup.length > 1 ? (
                <Box sx={candidateFilterSx.chipRailOrGroup}>
                  {chipGroup.map((chip, chipIndex) => (
                    <React.Fragment key={chip.key}>
                      {chipIndex > 0 && (
                        <CandidateFilterConnector
                          word="Or"
                          onClick={() =>
                            onSplitChipGroup?.(groupIndex, chipIndex - 1)
                          }
                        />
                      )}
                      {renderChip(chip)}
                    </React.Fragment>
                  ))}
                </Box>
              ) : (
                renderChip(chipGroup[0])
              )}
            </React.Fragment>
          ))}
        </Box>
      )}

      <Box sx={candidateFilterSx.chipRailActions}>
        {onSearchHistory && (
          <Link
            component="button"
            onClick={onSearchHistory}
            disabled={isReplying}
            sx={linkSx}
          >
            Search history
          </Link>
        )}
        {onNewSearch && (
          <Link
            component="button"
            onClick={onNewSearch}
            disabled={isReplying}
            sx={linkSx}
          >
            New search
          </Link>
        )}
        {(hasFilters || chipGroups.length > 0) && (
          <Link component="button" onClick={onClearAll} sx={linkSx}>
            Clear all filters
          </Link>
        )}
        <Link component="button" onClick={onResetTableLayout} sx={linkSx}>
          Reset table layout
        </Link>
      </Box>
    </Box>
  );
};
